import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  Download,
  Eye,
  Edit,
  Car,
  FileText,
  Smartphone,
  User,
  Loader2,
} from "lucide-react";
import { createRoot } from "react-dom/client";
import { toast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"; // or use shadcn/modal
import VehiclePDFView from "./VehiclePDFView";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import EditRegistrationModal from "./EditRegistrationModal";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

type VehicleRegistration = {
  registration_type: string;
  mt: string;
  id: string;
  date: string;
  registration_number: string;
  owner_name: string;
  mobile: string;
  alternate_mobile?: string;
  district: string;
  imei: string;
  sim_number1: string;
  sim_number2: string;
  sim_start: string;
  sim_end: string;
  rc_copy_path: string;
  id_proof_path: string;
  status: string; // "Active", "Inactive" or "Pending"
};

const downloadCombinedPDF = async (
  data: VehicleRegistration,
  setDownloadLoading?: (b: boolean) => void
) => {
  // 1. Generate first page from VehiclePDFView
  if (setDownloadLoading) setDownloadLoading(true);
  toast({
    title: "Preparing PDF...",
    description: "Please wait while your PDF is being generated.",
  });
  const container = document.createElement("div");
  document.body.appendChild(container);
  const tempRef = React.createRef<HTMLDivElement>();
  const root = createRoot(container);

  const htmlPdfBlob: Blob = await new Promise((resolve) => {
    root.render(
      <VehiclePDFView
        ref={tempRef}
        data={data}
        sim_start={data.sim_start}
        sim_end={data.sim_end}
        setPreviewData={() => {}}
      />
    );
    setTimeout(() => {
      (async () => {
        if (tempRef.current) {
          const canvas = await html2canvas(tempRef.current);
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF({
            orientation: "portrait",
            unit: "px",
            format: [canvas.width, canvas.height],
          });
          pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
          const blob = pdf.output("blob");
          resolve(blob);
        }
        root.unmount();
        document.body.removeChild(container);
      })();
    }, 100);
  });

  // 2. Fetch RC and ID files
  const rcFileName = data.rc_copy_path.split(/[\\/]/).pop()!;
  const idFileName = data.id_proof_path.split(/[\\/]/).pop()!;
  const rcUrl = `${
    import.meta.env.VITE_API_URL
  }/api/vehicle/file/rc_copies/${rcFileName}`;
  const idUrl = `${
    import.meta.env.VITE_API_URL
  }/api/vehicle/file/id_proofs/${idFileName}`;

  const [rcRes, idRes] = await Promise.all([
    fetch(rcUrl, { credentials: "include" }),
    fetch(idUrl, { credentials: "include" }),
  ]);

  if (!rcRes.ok || !idRes.ok) {
    toast({
      title: "Error",
      description: "RC or ID file not found or unauthorized",
      variant: "destructive",
    });
    return;
  }

  const rcBlob = await rcRes.blob();
  const idBlob = await idRes.blob();

  // 3. Create new PDF and merge all 3 pages
  const finalPdf = await PDFDocument.create();

  // Add Page 1 from html2canvas
  const htmlPdf = await PDFDocument.load(await htmlPdfBlob.arrayBuffer());
  const [firstPage] = await finalPdf.copyPages(htmlPdf, [0]);
  finalPdf.addPage(firstPage);

  // Helper: Add image or PDF to final PDF
  const addFileToPDF = async (fileBlob: Blob) => {
    const mime = fileBlob.type;

    if (mime === "application/pdf") {
      const externalPdf = await PDFDocument.load(await fileBlob.arrayBuffer());
      const pages = await finalPdf.copyPages(
        externalPdf,
        externalPdf.getPageIndices()
      );
      pages.forEach((page) => finalPdf.addPage(page));
    } else if (mime.startsWith("image/")) {
      const imageBytes = await fileBlob.arrayBuffer();
      let img;
      if (mime === "image/jpeg" || mime === "image/jpg") {
        img = await finalPdf.embedJpg(imageBytes);
      } else {
        img = await finalPdf.embedPng(imageBytes);
      }

      const page = finalPdf.addPage([img.width, img.height]);
      page.drawImage(img, {
        x: 0,
        y: 0,
        width: img.width,
        height: img.height,
      });
    }
  };

  await addFileToPDF(rcBlob);
  await addFileToPDF(idBlob);

  const mergedBlob = await finalPdf.saveAsBase64({ dataUri: true });

  // 4. Trigger download
  const link = document.createElement("a");
  link.href = mergedBlob;
  link.download = `Vehicle_Registration_${data.registration_number}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  if (setDownloadLoading) setDownloadLoading(false);
  toast({
    title: "PDF Downloaded",
    description: `Registration ${data.registration_number} PDF with documents downloaded.`,
  });
};

interface VehicleRegistrationTableProps {
  userRole: "staff" | "admin";
  userName: string;
  userId: number;
}

const VehicleRegistrationTable = ({
  userRole,
  userName,
  userId,
}: VehicleRegistrationTableProps) => {
  const [previewData, setPreviewData] = useState<VehicleRegistration | null>(
    null
  );
  const [editData, setEditData] = useState<VehicleRegistration | null>(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState<string | null>(
    null
  );
  const [refreshFlag, setRefreshFlag] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  const formatDate = (isoDate: string) => {
    if (!isoDate) return "";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [vehicleRegistrations, setVehicleRegistrations] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0); // filtered count
  const [totalCount, setTotalCount] = useState(0); // unfiltered total
  const [loading, setLoading] = useState(false);

  // Debounce searchTerm -> debouncedSearchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    let ignore = false;
    const fetchRegistrations = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/api/vehicle/registrations`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              userRole,
              userId,
              page: pageIndex + 1,
              pageSize,
              searchTerm: debouncedSearchTerm,
            }),
          }
        );
        if (!response.ok)
          throw new Error("Failed to fetch vehicle registrations");
        const data = await response.json();
        if (!ignore) {
          setVehicleRegistrations(data.vehicles || []);
          setRowCount(data.total || 0);
          // If backend provides unfilteredTotal, use it; else fallback to total if no search
          if (typeof data.unfilteredTotal === "number") {
            setTotalCount(data.unfilteredTotal);
          } else if (!debouncedSearchTerm) {
            setTotalCount(data.total || 0);
          }
        }
      } catch (err) {
        if (!ignore) {
          setVehicleRegistrations([]);
          setRowCount(0);
          toast({
            title: "Error",
            description:
              err.message || "Could not fetch vehicle registrations.",
            variant: "destructive",
          });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchRegistrations();
    return () => {
      ignore = true;
    };
  }, [userRole, userId, pageIndex, pageSize, debouncedSearchTerm, refreshFlag]);

  // Define columns for TanStack Table
  const columns = useMemo(
    () => [
      {
        header: "ID",
        cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
      },
      {
        header: "Date",
        accessorKey: "date",
        cell: ({ getValue }) => formatDate(getValue()),
      },
      {
        header: "Reg. No",
        accessorKey: "registration_number",
      },
      {
        header: "Owner",
        accessorKey: "owner_name",
      },
      {
        header: "Mobile",
        accessorKey: "mobile",
      },
      {
        header: "District",
        accessorKey: "district",
      },
      {
        header: "IMEI",
        accessorKey: "imei",
      },
      {
        header: "SIM Airtel",
        accessorKey: "sim_number1",
      },
      {
        header: "SIM BSNL",
        accessorKey: "sim_number2",
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }) => (
          <Badge variant={getValue() === "Active" ? "default" : "secondary"}>
            {getValue()}
          </Badge>
        ),
      },
      {
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPreviewData(row.original)}
            >
              <Eye className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditData(row.original)}
            >
              <Edit className="w-4 h-4" />
            </Button>
            {userRole === "admin" && (
              <Button
                variant="outline"
                size="sm"
                disabled={downloadLoadingId === row.original.id}
                onClick={async () => {
                  setDownloadLoadingId(row.original.id);
                  await downloadCombinedPDF(row.original, () =>
                    setDownloadLoadingId(null)
                  );
                  setDownloadLoadingId(null);
                }}
              >
                {downloadLoadingId === row.original.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            )}
          </div>
        ),
      },
    ],
    [pageIndex, pageSize, downloadLoadingId, userRole]
  );

  const table = useReactTable({
    data: vehicleRegistrations,
    columns,
    pageCount: Math.ceil(rowCount / pageSize),
    state: {
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const formSections = [
    {
      title: "Vehicle Information",
      icon: <Car className="w-5 h-5" />,
      fields: [
        { label: "Registration Type", key: "registration_type", type: "text" },
        { label: "MT (Metric Ton)", key: "mt", type: "text" },
        { label: "District", key: "district", type: "text" },
        {
          label: "Vehicle Registration Number",
          key: "registration_number",
          type: "text",
        },
      ],
    },
    {
      title: "Owner Information",
      icon: <User className="w-5 h-5" />,
      fields: [
        { label: "Vehicle Owner Name", key: "owner_name", type: "text" },
        { label: "Vehicle Owner Mobile Number", key: "mobile", type: "tel" },
        {
          label: "Alternate Mobile Number",
          key: "alternate_mobile",
          type: "tel",
        },
      ],
    },
    {
      title: "Device Information",
      icon: <Smartphone className="w-5 h-5" />,
      fields: [
        { label: "IMEI", key: "imei", type: "text" },
        { label: "SIM Number Airtel", key: "sim_number1", type: "text" },
        { label: "SIM Number BSNL", key: "sim_number2", type: "text" },
        { label: "SIM Validity Start", key: "sim_start", type: "date" },
        { label: "SIM Validity End", key: "sim_end", type: "date" },
      ],
    },
    {
      title: "Status & Documents",
      icon: <FileText className="w-5 h-5" />,
      fields: [
        // { label: "RC Copy", key: "rc_copy_path", type: "text" },
        // { label: "ID Proof", key: "id_proof_path", type: "text" },
        // { label: "Status", key: "status", type: "text" },
      ],
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search registrations..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageIndex(0);
            }}
            className="pl-10 w-64"
          />
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export All
        </Button>
      </div>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="text-center">
                {loading ? "Loading..." : "No data"}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {/* Entry summary */}
      <div className="text-sm text-muted-foreground mt-2">
        {(() => {
          const start = rowCount === 0 ? 0 : pageIndex * pageSize + 1;
          const end =
            rowCount === 0 ? 0 : Math.min((pageIndex + 1) * pageSize, rowCount);
          if (searchTerm && totalCount > rowCount) {
            return `Showing ${start} to ${end} of ${rowCount} entries (filtered from ${totalCount} total entries)`;
          } else {
            return `Showing ${start} to ${end} of ${rowCount} entries`;
          }
        })()}
      </div>
      {/* Pagination Controls */}
      <div className="flex justify-end items-center gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPageIndex(0)}
          disabled={pageIndex === 0}
        >
          First
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPageIndex((old) => Math.max(0, old - 1))}
          disabled={pageIndex === 0}
        >
          Previous
        </Button>
        <span>
          Page {pageIndex + 1} of {Math.max(1, Math.ceil(rowCount / pageSize))}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setPageIndex((old) => old + 1)}
          disabled={pageIndex + 1 >= Math.ceil(rowCount / pageSize)}
        >
          Next
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() =>
            setPageIndex(Math.max(0, Math.ceil(rowCount / pageSize) - 1))
          }
          disabled={pageIndex + 1 >= Math.ceil(rowCount / pageSize)}
        >
          Last
        </Button>
        <span className="ml-4">Rows per page:</span>
        <select
          value={pageSize}
          onChange={(e) => {
            setPageSize(Number(e.target.value));
            setPageIndex(0);
          }}
          className="border rounded px-2 py-1"
        >
          {[10, 20, 50, 100].map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>
      {/* PDF Preview Dialog */}
      {previewData && (
        <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
          <VisuallyHidden>
            <DialogTitle>Registration Form Preview</DialogTitle>
            <div id="edit-dialog-desc">
              Preview the details of the selected vehicle registration.
            </div>
          </VisuallyHidden>
          <DialogContent className="max-w-4xl">
            <div className="overflow-auto max-h-[80vh]">
              <VehiclePDFView
                ref={printRef}
                data={previewData}
                sim_start={previewData.sim_start}
                sim_end={previewData.sim_end}
                setPreviewData={setPreviewData}
              />
            </div>
            {userRole === "admin" && (
              <Button
                className="mt-4"
                onClick={async () => {
                  if (printRef.current) {
                    const canvas = await html2canvas(printRef.current);
                    const imgData = canvas.toDataURL("image/png");
                    const pdf = new jsPDF({
                      orientation: "portrait",
                      unit: "px",
                      format: [canvas.width, canvas.height],
                    });
                    pdf.addImage(
                      imgData,
                      "PNG",
                      0,
                      0,
                      canvas.width,
                      canvas.height
                    );
                    pdf.save(
                      `Registration_${previewData.registration_number}.pdf`
                    );
                  }
                }}
                aria-describedby="edit-dialog-desc"
              >
                Print / Download
              </Button>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Registration Modal */}
      {editData && (
        <Dialog open={!!editData} onOpenChange={() => setEditData(null)}>
          <VisuallyHidden>
            <DialogTitle>Edit Vehicle Registration</DialogTitle>
            <div id="edit-dialog-desc">
              Edit the details of the selected vehicle registration. All changes
              will be saved after you click Save.
            </div>
          </VisuallyHidden>
          <DialogContent className="max-w-5xl p-0 h-[90vh] flex flex-col bg-background">
            <EditRegistrationModal
              editData={editData}
              setEditData={setEditData}
              formSections={formSections}
              userRole={userRole}
              onEditSuccess={() => {
                setRefreshFlag((f) => !f);
              }}
              aria-describedby="edit-dialog-desc"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default VehicleRegistrationTable;
